import { useDebounceFn } from "ahooks"
import { useCallback, useEffect } from "react"

interface UseInfiniteScrollOptions<T extends HTMLElement> {
	target: T | null
	hasMore: boolean
	isLoading: boolean
	threshold?: number
	debounceTime?: number
	direction?: "vertical" | "left-to-right" | "right-to-left"
	onLoadMore: () => void
}

export const useInfiniteScroll = <T extends HTMLElement>({
	target,
	hasMore,
	isLoading,
	threshold = 20,
	debounceTime = 100,
	direction = "vertical",
	onLoadMore,
}: UseInfiniteScrollOptions<T>) => {
	const checkScrollPosition = useCallback(() => {
		if (!target) return false

		if (direction === "vertical") {
			return (
				target.scrollHeight - (target.scrollTop + target.clientHeight) <=
				threshold
			)
		} else if (direction === "right-to-left") {
			// For left-to-right, trigger when near the start of the scroll
			return target.scrollLeft <= threshold
		} else if (direction === "left-to-right") {
			// For right-to-left, trigger when near the end of the scroll
			const scrollMax = target.scrollWidth - target.clientWidth
			return scrollMax - target.scrollLeft <= threshold
		}
		return false
	}, [target, threshold, direction])
	const debouncedLoadMore = useDebounceFn(
		() => {
			if (!target || !checkScrollPosition()) {
				return
			} else {
				onLoadMore()
				if (direction === "right-to-left") {
					target.scrollBy({ left: threshold })
				}
			}
		},
		{ wait: debounceTime },
	)

	const handleScroll = useCallback(() => {
		if (!hasMore || isLoading) return
		debouncedLoadMore.run()
	}, [hasMore, isLoading, debouncedLoadMore])

	useEffect(() => {
		target?.addEventListener("scroll", handleScroll)
		return () => {
			target?.removeEventListener("scroll", handleScroll)
		}
	}, [handleScroll, target])

	return null
}
